import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SITE, NAV_LINKS } from "@/lib/site";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        scrolled
          ? "bg-zinc-950/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-32 sm:h-40 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo-link"
          className="flex items-center gap-2 group overflow-visible"
        >
          <img
            src={SITE.logo}
            alt="Lemonsqueezy Marketing Labs"
            className="h-12 sm:h-20 w-auto scale-[1.35] origin-left -ml-1"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm text-zinc-300 hover:text-yellow-400 transition-colors font-medium"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="#contact"
            data-testid="nav-cta-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-yellow-400 text-zinc-950 text-sm font-bold hover:bg-yellow-300 transition-all hover:-translate-y-0.5"
          >
            Get a free audit
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden p-2 text-zinc-200 hover:text-yellow-400"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-zinc-950/95 backdrop-blur-xl border-t border-white/10"
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="text-base text-zinc-200 hover:text-yellow-400 transition"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-cta"
              className="mt-2 inline-flex items-center justify-center px-5 py-3 rounded-full bg-yellow-400 text-zinc-950 font-bold"
            >
              Get a free audit
            </a>
          </div>
        </div>
      )}
    </motion.header>
  );
}
